'use client';

import { useEffect, useEffectEvent } from 'react';

// Type definitions for requestIdleCallback
type IdleCallbackHandle = number;
type IdleRequestCallback = (deadline: IdleDeadline) => void;

type IdleDeadline = {
  didTimeout: boolean;
  timeRemaining: () => number;
};

// Polyfill for browsers that don't support requestIdleCallback
const requestIdleCallbackPolyfill = (callback: IdleRequestCallback): IdleCallbackHandle => {
  const start = Date.now();
  return window.setTimeout(() => {
    callback({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
    });
  }, 1);
};

const cancelIdleCallbackPolyfill = (handle: IdleCallbackHandle) => {
  window.clearTimeout(handle);
};

// Use native implementation if available, otherwise use polyfill
const requestIdleCallbackFn =
  typeof window !== 'undefined'
    ? (window as any).requestIdleCallback || requestIdleCallbackPolyfill
    : requestIdleCallbackPolyfill;

const cancelIdleCallbackFn =
  typeof window !== 'undefined'
    ? (window as any).cancelIdleCallback || cancelIdleCallbackPolyfill
    : cancelIdleCallbackPolyfill;

/**
 * Custom hook that executes a callback during browser idle periods
 * @param callback Function to execute during idle periods
 * @param deps Dependency array that determines when to re-schedule the callback
 */
export function useIdleCallback(callback: IdleRequestCallback, deps: React.DependencyList = []) {
  const onIdle = useEffectEvent(callback);

  useEffect(() => {
    // Schedule the idle callback
    const handle = requestIdleCallbackFn((deadline: IdleDeadline) => {
      onIdle(deadline);
    });

    // Clean up by canceling the callback
    return () => {
      cancelIdleCallbackFn(handle);
    };
  }, deps);
}
